import { useFindOne, useSession } from "@gadgetinc/react";
import { api } from "../api";

/**
 * Custom hook that fetches the current user with proper roles field selection
 * to avoid GraphQL validation errors. This replaces the problematic useUser(api) hook.
 * 
 * @returns {Object} The current user object or null if not signed in
 */
export const useUserWithRoles = () => {
  const session = useSession(api);
  
  const [{ data: user, fetching, error }, refetch] = useFindOne(
    api.user, 
    session?.user?.id || "", 
    {
      select: {
        id: true,
        createdAt: true,
        updatedAt: true,
        email: true,
        emailVerified: true,
        firstName: true,
        lastName: true,
        googleImageUrl: true,
        googleProfileId: true,
        lastSignedIn: true,
        roles: {
          key: true,
          name: true
        },
        approvalStatus: true,
        approvedAt: true,
        approvedById: true,
        deniedReason: true,
        requestNotes: true,
        assistantAccessMode: true,
        accessibleAssistantIds: true,
        assistantAccessNotes: true,
        adminNotes: {
          markdown: true,
          truncatedHTML: true
        },
        lastAccessCodeUsed: true,
        accessCodeRedeemedAt: true,
        accessCodeAtRegistration: true,
        registrationNotes: true,
        institution: true,
        registrationCompletedAt: true,
        canPrintSaveChats: true,
        invitedAt: true,
        invitationData: true,
        invitationToken: true,
        invitationTokenExpiration: true,
        invitedById: true,
        institutionRole: true,
        cohortId: true,
        fieldInternEligible: true,
        schedulerPermissions: true,
        globalSchedulerAccess: true,
        calendarAccess: true,
        patientEncounterAccess: true,
        subscriptionExpiration: true,
        subscriptionNotes: true,
        subscriptionGrantedAt: true,
        subscriptionStatus: true,
        subscriptionGrantedById: true,
        documentProcessingAccess: true,
        enrollmentRestrictions: true,
        graduatedCohorts: true,
        shiftEligibility: true,
        activeCohorts: true,
        individualAccessGrants: true,
        creatorGrantedById: true,
        agentsCreated: true,
        maxAgentsAllowed: true,
        isCreator: true,
        creatorGrantedAt: true,
        creatorNotes: true,
        accessibleAgentNames: true,
        agentAccessMode: true,
        agentAccessNotes: true
      },
      // Skip the request if there's no user ID from session
      skip: !session?.user?.id
    }
  );

  // Return null if no session or user
  if (!session?.user?.id) {
    return null;
  }

  // Return the user data (will be undefined while fetching, null if error)
  return user || null;
};